import { Box, Typography, Button, TextField, Avatar, Grid } from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import OrderHistoryTable from './OrderHistoryTable'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { IFacebookChatRoom } from '../../../types/facebook'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SelectSearch, { IOptionSelect } from '../../../components/SelectSearch'
import { IAddress, IAddressThailand } from '../../../types/address'
import { useEffect, useState } from 'react'
import useAxios, { axiosInstance } from '../../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { message } from 'antd'
import { parseThaiAddress } from '../../../utils/address'

interface Props {
  chatRoom?: IFacebookChatRoom | null
  mutate: () => void
}

const CustomerPanel = (props: Props) => {
  const { chatRoom, mutate } = props
  const [searchAddress, setSearchAddress] = useState<string>('')
  const [formatAddress, setFormatAddress] = useState<string>('')

  const addressList = useAxios<IAddressThailand[]>({
    method: 'GET',
    url: '/address/thailand-address',
    params: {
      page: 1,
      perPage: 5,
      search: searchAddress,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchAddress = async (value: string) => {
    setSearchAddress(value)
    await addressList.mutate()
  }

  const formik = useFormik<any>({
    initialValues: {
      fullName: '',
      phone: '',
      email: '',
      address: '',
      note: '',
      subDistrict: '',
      district: '',
      province: '',
      zipcode: '',
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required('กรุณากรอกชื่อเต็ม'),
      phone: Yup.string().required('กรุณากรอกเบอร์โทรศัพท์'),
      email: Yup.string().email('อีเมลไม่ถูกต้อง'),
      address: Yup.string().required('กรุณากรอกที่อยู่'),
      note: Yup.string().nullable().notRequired(),
      subDistrict: Yup.string().required('กรุณากรอกตำบล'),
      district: Yup.string().required('กรุณากรอกอำเภอ'),
      province: Yup.string().required('กรุณากรอกจังหวัด'),
      zipcode: Yup.string().required('กรุณากรอกรหัสไปรษณีย์'),
    }),
    onSubmit: async (values: any) => {
      if (!values.fullName) {
        message.error('กรุณากรอกชื่อเต็ม')
        return
      }
      try {
        const reponse = await axiosInstance.request({
          method: 'POST',
          url: '/facebook/pages/chatrooms/info',
          data: {
            id: chatRoom?.objectId,
            ...values,
          },
        })

        message.success('บันทึกข้อมูลสำเร็จ')
        mutate?.()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
    },
  })

  useEffect(() => {
    if (chatRoom) {
      formik.setValues({
        fullName: chatRoom.fullName || '',
        phone: chatRoom.phone || '',
        email: chatRoom.email || '',
        address: chatRoom.address || '',
        note: chatRoom.note || '',
        subDistrict: chatRoom.subDistrict || '',
        district: chatRoom.district || '',
        province: chatRoom.province || '',
        zipcode: chatRoom.zipcode || '',
      })
    }
  }, [chatRoom])

  const formatAddressSubmit = async () => {
    try {
      const addressSplit = parseThaiAddress(formatAddress)
      const { name, phone, address, subDistrict, district, province, zipcode } =
        addressSplit
      handleSelectAddress({
        name: name,
        phoneNumber: phone,
        address,
        subDistrict,
        district,
        province,
        postcode: zipcode,
      } as IAddress)
    } catch (e) {
      return
    }
  }

  const handleSelectAddress = (address?: IAddress) => {
    formik.setFieldValue('fullName', address?.name ?? '')
    formik.setFieldValue('phone', address?.phoneNumber ?? '')
    formik.setFieldValue('address', address?.address ?? '')
    formik.setFieldValue('subDistrict', address?.subDistrict ?? '')
    formik.setFieldValue('district', address?.district ?? '')
    formik.setFieldValue('province', address?.province ?? '')
    formik.setFieldValue('zipcode', address?.postcode ?? '')
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="h6">ลูกค้า #{chatRoom?.name}</Typography>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={12}>
            <TextField
              fullWidth
              value={formatAddress}
              label={`ตัวช่วยจำแนกที่อยู่`}
              multiline
              placeholder={`ตัวอย่างการกรอกข้อมูล
คุณมานี มานะ
0831725655
69/429 หมู่ 2 ต.ช้างเผือก อ.เมืองเชียงใหม่ จ.เชียงใหม่ 50300`}
              rows={5}
              onChange={(e) => setFormatAddress(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <Button
              fullWidth
              startIcon={<AutoFixHighIcon />}
              color="primary"
              size="medium"
              variant="contained"
              onClick={() => formatAddressSubmit()}
            >
              นำเข้าที่อยู่
            </Button>
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              id="username"
              fullWidth
              label="ชื่อ-นามสกุล"
              name="fullName"
              helperText={
                formik.touched.fullName && (formik.errors.fullName as string)
              }
              error={!!(formik.touched.fullName && formik.errors.fullName)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.fullName}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="phone"
              fullWidth
              label="เบอร์โทร"
              name="phone"
              helperText={
                formik.touched.phone && (formik.errors.phone as string)
              }
              error={!!(formik.touched.phone && formik.errors.phone)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.phone}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="email"
              fullWidth
              label="อีเมล"
              name="email"
              helperText={
                formik.touched.email && (formik.errors.email as string)
              }
              error={!!(formik.touched.email && formik.errors.email)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.email}
            />
          </Grid>

          <Grid item xs={12} lg={12}>
            <TextField
              id="address"
              fullWidth
              label="ที่อยู่"
              name="address"
              helperText={
                formik.touched.address && (formik.errors.address as string)
              }
              error={!!(formik.touched.address && formik.errors.address)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.address}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <SelectSearch<IAddressThailand>
              label={'ค้นหาที่อยู่'}
              onSelect={(e) => {
                formik.setFieldValue('subDistrict', e?.value?.subDistrict)
                formik.setFieldValue('district', e?.value?.district)
                formik.setFieldValue('province', e?.value?.province)
                formik.setFieldValue('zipcode', e?.value?.zipcode)
              }}
              search={searchAddress}
              onSearch={handleSearchAddress}
              options={
                addressList.data?.map((address: IAddressThailand) => ({
                  label: `${address.subDistrict} ${address.district} ${address.province} ${address.zipcode}`,
                  value: address,
                })) || ([] as IOptionSelect<IAddressThailand>[])
              }
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="subDistrict"
              fullWidth
              label="ตำบล"
              name="subDistrict"
              helperText={
                formik.touched.subDistrict &&
                (formik.errors.subDistrict as string)
              }
              error={
                !!(formik.touched.subDistrict && formik.errors.subDistrict)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.subDistrict}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="district"
              fullWidth
              label="อำเภอ"
              name="district"
              helperText={
                formik.touched.district && (formik.errors.district as string)
              }
              error={!!(formik.touched.district && formik.errors.district)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.district}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="province"
              fullWidth
              label="จังหวัด"
              name="province"
              helperText={
                formik.touched.province && (formik.errors.province as string)
              }
              error={!!(formik.touched.province && formik.errors.province)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.province}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="zipcode"
              fullWidth
              label="รหัสไปรษณีย์"
              name="zipcode"
              helperText={
                formik.touched.zipcode && (formik.errors.zipcode as string)
              }
              error={!!(formik.touched.zipcode && formik.errors.zipcode)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.zipcode}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              id="note"
              fullWidth
              label="หมายเหตุ"
              name="note"
              helperText={formik.touched.note && (formik.errors.note as string)}
              error={!!(formik.touched.note && formik.errors.note)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.note}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <Button
              type="submit"
              disabled={formik.isSubmitting}
              fullWidth
              variant="contained"
              color="primary"
            >
              บันทึก
            </Button>
          </Grid>
        </Grid>
      </form>

      {/* <Box py={2} display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          ชื่อผู้ใช้
        </Typography>
        {renderSocialLink('T', 'JSCPY Code', 'https://www.google.com', 'Meta')}
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight="bold">
          ประวัติคำสั่งซื้อ
        </Typography>
        <OrderHistoryTable />
      </Box> */}
    </Box>
  )
}

export default CustomerPanel
